import { Component, OnInit, ViewChild, ViewEncapsulation } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { MatPaginator } from '@angular/material/paginator';
import { MatSort } from '@angular/material/sort';
import {
  ClientSideDataTable,
  Column,
  DialogService,
  OrganizationService,
  Store,
  StoreService,
  TitleService,
  User,
  UserFormComponent,
  UserService
} from '@ng-cloud/badger-core';
import { UntypedFormControl, UntypedFormGroup } from '@angular/forms';
import { MediaObserver } from '@ngbracket/ngx-layout';
import { tap } from 'rxjs/operators';
import { AuthorizationService } from '@ng-cloud/badger-core/services/authorization.service';

import * as _ from 'lodash';

@Component({
  selector: 'bt-users',
  templateUrl: './users.component.html',
  styleUrl: './users.component.scss',
  standalone: false,
  encapsulation: ViewEncapsulation.None
})
export class UsersComponent implements OnInit {
  @ViewChild(MatSort, { static: true }) sort: MatSort;
  @ViewChild(MatPaginator, { static: true }) paginator: MatPaginator;

  dataTable: ClientSideDataTable<User>;

  roleOptions: string[];
  stores: Store[] = [];
  currentUser: User;

  constructor(
    public dialog: MatDialog,
    private userService: UserService,
    private storeService: StoreService,
    private organizationService: OrganizationService,
    private dialogService: DialogService,
    public titleService: TitleService,
    public media: MediaObserver,
    private authService: AuthorizationService
  ) {
  }

  ngOnInit() {
    const currentUserId = this.authService.getCurrentUserId();

    this.titleService.setTitle('Users');

    this.userService.getUser(currentUserId).subscribe(fullUser => {
      this.currentUser = fullUser;

      this.createUsersTable();

      this.dataTable.sort = this.sort;
      this.dataTable.paginator = this.paginator;
      this.dataTable.mediaObserver = this.media;
      this.dataTable.filter = new UntypedFormGroup({
        filter: new UntypedFormControl()
      });

      this.storeService.getStores().subscribe(stores => {
        this.stores = stores;
        this.userService.getUsers().pipe(
          tap(users => users.forEach(user => {
            user.organizationId && this.organizationService.addOrganization(user).subscribe();
            user.homeStoreId && (user.store = _.find(this.stores, store => store.id === user.homeStoreId));
          }))
        ).subscribe(users => this.dataTable.data = users);
      });
    });

    this.userService.getRoles().subscribe(roles => this.roleOptions = roles);
  }

  private createUsersTable() {
    const columnDefs: Column[] = [];

    columnDefs.push(new Column('email'));
    columnDefs.push(new Column('firstName'));
    columnDefs.push(new Column('lastName'));

    if (this.currentUser.roles.includes('admin')) {
      columnDefs.push(new Column('organization.name'));
    }

    columnDefs.push(new Column('store.name'));
    columnDefs.push(new Column('roles', { property: 'rolesString' }));
    columnDefs.push(new Column('lastSignInAt', { mediaQuery: 'gt-xs' }));
    columnDefs.push(new Column('addOrDelete'));

    this.dataTable = new ClientSideDataTable<User>(columnDefs);
  }

  deleteUser(user: User): void {
    this.dialogService.confirm(`Are you sure you want to delete "${user.email}"`).subscribe(confirmed => {
      if (confirmed) {
        this.userService.deleteUser(user).subscribe(() =>
          this.dataTable.data = this.dataTable.data.filter(u => u !== user)
        );
      }
    });
  }

  openCreate(): void {
    const newUser = new User();
    // Build simulation parameters (if properties are not set, pass null/empty array)
    const simulationParams = {
      organization_id: newUser.organizationId || null,
      roles: newUser.roles || []
    };

    this.userService.getPotentialStoreIds(simulationParams).subscribe(simulationResult => {
      const potentialStoreIds = simulationResult.potential_store_ids; // either an array or 'unrestricted'
      const dialogRef = this.dialog.open(UserFormComponent, {
        data: {
          user: newUser,
          roleOptions: this.roleOptions,
          potentialStoreIds: potentialStoreIds
        }
      });

      dialogRef.afterClosed().subscribe((result: User) => {
        if (result) {
          this.dataTable.data = this.dataTable.data.concat([result]);
        }
      });
    });
  }

  openEdit(user): void {
    this.userService.getUser(user.id, { include_allowed_store_data: 'true' })
      .subscribe(fullUserData => {

        const simulationParams = {
          organization_id: fullUserData.organizationId,
          roles: fullUserData.roles,
          user_id: user.id
        };

        this.userService.getPotentialStoreIds(simulationParams).subscribe(simulationResult => {
          const potentialStoreIds = simulationResult.potential_store_ids;

          const dialogRef = this.dialog.open(UserFormComponent, {
            data: {
              user: fullUserData,
              roleOptions: this.roleOptions,
              potentialStoreIds: potentialStoreIds
            }
          });

          dialogRef.afterClosed().subscribe((result: User) => {
            if (result) {
              Object.assign(this.dataTable.data.find(r => result.id == r.id), result);
            }
          });
        });
      });
  }

  canEditOrDelete(user: any): boolean {

    // User is not rank restricted
    if (!this.currentUser.maxRoleRank) {
      return true;
    }

    // Can edit their own user
    if (this.currentUser.id == user.id) {
      return true;
    }

    // Allow if the current user has a higher rank than the target user
    return this.currentUser.maxRoleRank > user.maxRoleRank;
  }

}

